import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PageHero, Container, Overline } from "../components/Section";
import { ArrowRight } from "lucide-react";

const ABOUT_IMG_1 = "/images/branding/about-church.jpg";
const ABOUT_IMG_2 = "/images/history/smzbt-01.jpg";
const ABOUT_IMG_3 = "/images/history/smzbt-02.jpg";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        overline="Who We Are"
        title={<>A Family. A Movement. <em className="text-gold-light font-light italic">A Home.</em></>}
        kicker="Sixth Mount Zion Baptist Temple is a Christ-centered congregation in Hampton, Virginia, devoted to the gospel and to one another."
        image="/images/branding/congregation-family.jpg"
      />

      <Container className="py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <p className="drop-cap text-lg md:text-xl leading-[1.8] text-white/85 font-serif first-line:tracking-wider">
              From its earliest days, Sixth Mount Zion has been a sanctuary — not only a place of refuge, but a place where lives are transformed by the unrelenting love of God. We gather every week to lift our voices in worship, to open the scriptures, and to share burdens and breakthroughs as one family.
            </p>
            <p className="mt-6 text-white/75 leading-relaxed text-lg">
              Our community spans generations. From the children in Vacation Bible School to the elders whose prayers have shaped this house, every member matters. We believe the local church is God's primary plan for changing the world, one disciple at a time.
            </p>
            <p className="mt-6 text-white/75 leading-relaxed text-lg">
              Whether you are exploring faith for the first time, returning after a long road, or rooted deep — there is a seat reserved for you here.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/history" className="btn-wine px-6 py-3 rounded-full text-sm uppercase tracking-wider inline-flex items-center gap-2" data-testid="about-history-cta">
                Our History <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/pastor" className="btn-ghost px-6 py-3 rounded-full text-sm uppercase tracking-wider inline-flex items-center gap-2">
                Senior Pastor <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-3">
              <motion.img
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9 }}
                src={ABOUT_IMG_1}
                alt="Pastor leading worship"
                className="col-span-2 w-full h-[360px] object-cover"
              />
              <motion.img
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 0.1 }}
                src={ABOUT_IMG_2}
                alt="Worship moment"
                className="w-full h-[220px] object-cover"
              />
              <motion.img
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 0.2 }}
                src={ABOUT_IMG_3}
                alt="Congregation in worship"
                className="w-full h-[220px] object-cover"
              />
            </div>
          </div>
        </div>
      </Container>

      <section className="bg-wine-950 py-24">
        <Container>
          <Overline>Our Beliefs</Overline>
          <h2 className="text-4xl md:text-5xl font-sans font-black mb-14 max-w-2xl tracking-[-0.02em]">We believe the gospel of Jesus Christ is good news for <span className="brand-gradient-text">all people</span>.</h2>
          <div className="grid md:grid-cols-3 gap-px bg-white/5">
            {[
              { title: "The Scriptures", scripture: "2 Timothy 3:16–17", body: "We hold to the inerrancy and authority of the Bible as the inspired Word of God for faith and practice." },
              { title: "The Trinity", scripture: "Matthew 28:19", body: "We believe in one God — eternally existing in three persons: Father, Son, and Holy Spirit." },
              { title: "Jesus Christ", scripture: "John 1:14 · 1 Cor 15:3–4", body: "Fully God and fully man, Jesus lived, died, and rose to bring us into right relationship with the Father." },
              { title: "Salvation", scripture: "Ephesians 2:8–9", body: "Salvation comes by grace through faith in Christ alone — a free gift to all who believe." },
              { title: "The Church", scripture: "1 Corinthians 12:27", body: "The local church is the body of Christ, called to worship, disciple, and serve the world." },
              { title: "The Return", scripture: "Acts 1:11 · Rev 22:12", body: "Jesus will return personally, visibly, and gloriously to receive His own and judge the living and dead." },
            ].map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
                className="bg-wine-950 p-10"
              >
                <div className="text-xs uppercase tracking-[0.25em] brand-gradient-text-warm font-extrabold mb-4">{b.scripture}</div>
                <h3 className="font-sans text-2xl font-extrabold mb-3">{b.title}</h3>
                <p className="text-white/70 leading-relaxed">{b.body}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>
    </div>
  );
}
